import React, { useState, useEffect, useRef } from "react";
import { Button, Col, Row, Form, Container } from "react-bootstrap";
import jsDig from "utils/jsDig";
import { v4 as uuidv4 } from "uuid";
import { capitalize } from "lodash-es";
import CustomTooltip from "utils/CustomTooltip";
import PlatformSpecific from "./PlatformSpecific";

export default function ApiAccess(props) {
  const [mccId, setMccId] = useState(props.organization.google_ads_mcc_id);
  const [googleAds, setGoogleAds] = useState(props.api_platforms.google_ads);
  const [microsoftAds, setMicrosoftAds] = useState(props.api_platforms.microsoft_ads);
  const [googleAnalytics, setGoogleAnalytics] = useState(props.api_platforms.google_analytics);
  const [googleWebmasters, setGoogleWebmasters] = useState(props.api_platforms.google_webmasters);
  const [facebookAds, setFacebookAds] = useState(props.api_platforms.facebook_ads);
  const [tiktokAds, setTiktokAds] = useState(props.api_platforms.tiktok_ads);
  const [showAlert, setShowAlert] = useState(
    (googleAds.authenticated === true && mccId === null) ||
      (googleAds.authenticated === true && mccId === undefined) ||
      (googleAds.authenticated === true && mccId.length !== 10)
  );
  //Below are states being used to facilitate rerendering component when each service is deauthenticated
  const [deAuthGA, setDeAuthGA] = useState(true);
  const [deAuthGAN, setDeAuthGAN] = useState(true);
  const [deAuthGW, setDeAuthGW] = useState(true);
  const [deAuthMA, setDeAuthMA] = useState(true);
  const [deAuthFA, setDeAuthFA] = useState(true);
  const [deAuthTT, setDeAuthTT] = useState(true);
  //Below are states being used to facilitate rerendering component when each service is authorizing
  const [authGA, setAuthGA] = useState(false);
  const [authGAN, setAuthGAN] = useState(false);
  const [authGW, setAuthGW] = useState(false);
  const [authMA, setAuthMA] = useState(false);
  const [authFA, setAuthFA] = useState(false);
  const [authTT, setAuthTT] = useState(false);
  //Below are refs for each service to ensure they don't auto useEffect on mount
  const didMountRefGA = useRef(false);
  const didMountRefGAN = useRef(false);
  const didMountRefGW = useRef(false);
  const didMountRefMA = useRef(false);
  const didMountRefFA = useRef(false);
  const didMountRefTT = useRef(false);
  //Below states are used to facilitate initial stage on page load, needs to be separate from top state items to prevent infininte loop of rerenders
  const [initialGoogleAdsState, setInitialGoogleAdsState] = useState(props.api_platforms.google_ads.authenticated);
  const [initialMicrosoftAdsState, setInitialMicrosoftAdsState] = useState(props.api_platforms.microsoft_ads.authenticated);
  const [initialGoogleAnalyticsState, setInitialGoogleAnalyticsState] = useState(props.api_platforms.google_analytics.authenticated);
  const [initialGoogleWebmastersState, setInitialGoogleWebmastersState] = useState(props.api_platforms.google_webmasters.authenticated);
  const [initialFacebookAdsState, setInitialFacebookAdsState] = useState(props.api_platforms.facebook_ads.authenticated);
  const [initialTiktokAdsState, setInitialTiktokAdsState] = useState(props.api_platforms.tiktok_ads.authenticated);
  
  useEffect(() => {
    if (
      (googleAds.authenticated === true && mccId === null) ||
      (googleAds.authenticated === true && mccId === undefined) ||
      (googleAds.authenticated === true && mccId.length !== 10)
    ) {
      setShowAlert(true);
      window.scrollTo(0, document.body.scrollHeight);
    } else {
      setShowAlert(false);
    }
  }, [googleAds, mccId, showAlert]);

  const authenticateFunction = (platform) => {
    window.open(platform["auth_url"], "_blank");
    if (platform.title === "Google Ads") {
      setAuthGA(!authGA);
      didMountRefGA.current = true;
    }
    if (platform.title === "Google Analytics") {
      setAuthGAN(!authGAN);
      didMountRefGAN.current = true;
    }
    if (platform.title === "Google Webmasters") {
      setAuthGW(!authGW);
      didMountRefGW.current = true;
    }
    if (platform.title === "Facebook Ads") {
      setAuthFA(!authFA);
      didMountRefFA.current = true;
    }
    if (platform.title === "Microsoft Ads") {
      setAuthMA(!authMA);
      didMountRefMA.current = true;
    }
    if (platform.title === "Tiktok Ads") {
      setAuthTT(!authTT);
      didMountRefTT.current = true;
    }
  };

  const deauthorizeFunction = (platform) => {
    window.open(platform["deauth_url"], "_blank");
    //below items are changing state items so useEffect can pick up on it to rerender automatically
    if (platform.title === "Google Ads") {
      didMountRefGA.current = true;
      setDeAuthGA(!deAuthGA);
      setGoogleAds({...googleAds});
    }
    if (platform.title === "Google Analytics") {
      didMountRefGAN.current = true;
      setDeAuthGAN(!deAuthGAN);
      setGoogleAnalytics({...googleAnalytics});
    }
    if (platform.title === "Google Webmasters") {
      didMountRefGW.current = true;
      setDeAuthGW(!deAuthGW);
      setGoogleWebmasters({...googleWebmasters});
    }
    if (platform.title === "Facebook Ads") {
      didMountRefFA.current = true;
      setDeAuthFA(!deAuthFA);
      setFacebookAds({...facebookAds});
    }
    if (platform.title === "Microsoft Ads") {
      didMountRefMA.current = true;
      setDeAuthMA(!deAuthMA);
      setMicrosoftAds({...microsoftAds});
    }
    if (platform.title === "Tiktok Ads") {
      didMountRefTT.current = true;
      setDeAuthTT(!deAuthTT);
      setTiktokAds({...tiktokAds});
    }
  };

  const resetMessage = (platformName) => {
    return `Resetting ${capitalize(platformName)} token will require you to authenticate again`;
  };

  const authButton = (platform) => {
    if (platform.authenticated) {
      return (
        <>
          <Button className="mb-4 btn-lg api-button text-center" variant="danger" onClick={() => deauthorizeFunction(platform)}>
            Reset Token
            <CustomTooltip faClass="text-white far fa-question-circle f18 pl-2" message={resetMessage(platform["title"])} inline={true} />
          </Button>
        </>
      );
    } else {
      if (platform.title == "Facebook Ads" || platform.title == "Microsoft Ads" || platform.title == "Tiktok Ads") {
        return (
          <>
            <Button className="mb-4 api-button btn-lg text-center" variant="success" onClick={() => authenticateFunction(platform)}>
              Authenticate
              <i className="fas fa-cog pl-2" style={{ paddingRight: "0px", color: "unset", float: "unset" }}></i>
            </Button>
          </>
        );
      } else {
        let googleBtn = require("images/google/btn_google_signin_light_normal_web.png");
        googleBtn = <img className="api-google-button mx-auto mb-4" onClick={() => authenticateFunction(platform)} src={`/datapulse${googleBtn}`} />;
        return <div onClick={() => authenticateFunction(platform)}>{googleBtn}</div>;
      }
    }
  };

  //Below items are for deauthorize state change to force rerender for each button
  useEffect(() => {
    if (props.api_platforms.google_ads.authenticated === true && googleAds.authenticated === true) {
      setGoogleAds((prevState) => ({ ...prevState, authenticated: props.api_platforms.google_ads.authenticated }));
    }
    if (props.api_platforms.google_ads.authenticated === false) {
      setGoogleAds((prevState) => ({ ...prevState, authenticated: props.api_platforms.google_ads.authenticated }));
    }
  }, [deAuthGA]);
  useEffect(() => {
    if (props.api_platforms.google_analytics.authenticated === true && googleAnalytics.authenticated === true) {
      setGoogleAnalytics((prevState) => ({ ...prevState, authenticated: props.api_platforms.google_analytics.authenticated }));
    }
    if (props.api_platforms.google_analytics.authenticated === false) {
      setGoogleAnalytics((prevState) => ({ ...prevState, authenticated: props.api_platforms.google_analytics.authenticated }));
    }
  }, [deAuthGAN]);
  useEffect(() => {
    if (props.api_platforms.google_webmasters.authenticated === true && googleWebmasters.authenticated === true) {
      setGoogleWebmasters((prevState) => ({ ...prevState, authenticated: props.api_platforms.google_webmasters.authenticated }));
    }
    if (props.api_platforms.google_webmasters.authenticated === false) {
      setGoogleWebmasters((prevState) => ({ ...prevState, authenticated: props.api_platforms.google_webmasters.authenticated }));
    }
  }, [deAuthGW]);
  useEffect(() => {
    if (props.api_platforms.facebook_ads.authenticated === true && facebookAds.authenticated === true) {
      setFacebookAds((prevState) => ({ ...prevState, authenticated: props.api_platforms.facebook_ads.authenticated}));
    }
    if (props.api_platforms.facebook_ads.authenticated === false) {
      setFacebookAds((prevState) => ({ ...prevState, authenticated: props.api_platforms.facebook_ads.authenticated}));
    }
  }, [deAuthFA]);
  useEffect(() => {
    if (props.api_platforms.microsoft_ads.authenticated === true && microsoftAds.authenticated === true) {
      setMicrosoftAds((prevState) => ({ ...prevState, authenticated: props.api_platforms.microsoft_ads.authenticated }));
    }
    if (props.api_platforms.microsoft_ads.authenticated === false) {
      setMicrosoftAds((prevState) => ({ ...prevState, authenticated: props.api_platforms.microsoft_ads.authenticated }));
    }
  }, [deAuthMA]);
  useEffect(() => {
    if (props.api_platforms.tiktok_ads.authenticated === true && tiktokAds.authenticated === true) {
      setTiktokAds((prevState) => ({ ...prevState, authenticated: props.api_platforms.tiktok_ads.authenticated }));
    }
    if (props.api_platforms.microsoft_ads.authenticated === false) {
      setTiktokAds((prevState) => ({ ...prevState, authenticated: props.api_platforms.tiktok_ads.authenticated }));
    }
  }, [deAuthTT]);

  //Below Items make Async calls to fetch authentication status for each platform and set it.  Will set authentication status regardless
  //if its to authenticate or deauthenticate.  Above useEffects are merely for triggering rerender on deauthenticate.

  //Google Ads
  useEffect(() => {
    if (didMountRefGA.current) {
      let count = 0;
      let googleAdsStatePlaceholder = props.api_platforms.google_ads.authenticated;

      async function getData() {
        fetch(`/datapulse/api-authentication-updater?service=google_ads`)
          .then((res) => res.json())
          .then((data) => (googleAdsStatePlaceholder = data.data))
          .then(function (newData) {
            if (newData == true) {
              setInitialGoogleAdsState(false);
            } else {
              setInitialGoogleAdsState(true);
            }
          });
      }
      let interval = setInterval(() => {
        if (count > 400 || googleAds.authenticated === true) {
          return () => {
            clearInterval(interval);
          };
        }
        count = count + 1;
        // console.log("count ga", count);
        getData();
        if (initialGoogleAdsState !== googleAdsStatePlaceholder) {
          setGoogleAds((prevState) => ({ ...prevState, authenticated: googleAdsStatePlaceholder }));
        }
      }, 500);
      return () => {
        clearInterval(interval);
      };
    }
  }, [authGA, googleAds]);

  //Google Analytics
  useEffect(() => {
    if (didMountRefGAN.current) {
      let count = 0;
      let googleAnalyticsStatePlaceholder = props.api_platforms.google_analytics.authenticated;

      async function getData() {
        fetch(`/datapulse/api-authentication-updater?service=google_analytics`)
          .then((res) => res.json())
          .then((data) => (googleAnalyticsStatePlaceholder = data.data))
          .then(function (newData) {
            if (newData == true) {
              setInitialGoogleAnalyticsState(false);
            } else {
              setInitialGoogleAnalyticsState(true);
            }
          });
      }

      let interval = setInterval(() => {
        if (count > 400 || googleAnalytics.authenticated === true) {
          return () => {
            clearInterval(interval);
          };
        }
        count = count + 1;
        // console.log("count gan", count);
        getData();
        if (initialGoogleAnalyticsState !== googleAnalyticsStatePlaceholder) {
          setGoogleAnalytics((prevState) => ({ ...prevState, authenticated: googleAnalyticsStatePlaceholder }));
        }
      }, 500);
      return () => {
        clearInterval(interval);
      };
    }
  }, [googleAnalytics, authGAN]);

  //Google Webmasters
  useEffect(() => {
    if (didMountRefGW.current) {
      let googleWebmastersStatePlaceholder = props.api_platforms.google_webmasters.authenticated;
      let count = 0;

      async function getData() {
        fetch(`/datapulse/api-authentication-updater?service=google_webmasters`)
          .then((res) => res.json())
          .then((data) => (googleWebmastersStatePlaceholder = data.data))
          .then(function (newData) {
            if (newData == true) {
              setInitialGoogleWebmastersState(false);
            } else {
              setInitialGoogleWebmastersState(true);
            }
          });
      }

      let interval = setInterval(() => {
        if (count > 400) {
          return;
        }
        count = count + 1;
        getData();
        if (initialGoogleWebmastersState !== googleWebmastersStatePlaceholder) {
          setGoogleWebmasters((prevState) => ({ ...prevState, authenticated: googleWebmastersStatePlaceholder }));
        }
      }, 500);
      return () => {
        clearInterval(interval);
      };
    }
  }, [googleWebmasters, authGW]);

  //Microsoft Ads
  useEffect(() => {
    if (didMountRefMA.current) {
      let microsoftAdsStatePlaceholder = props.api_platforms.microsoft_ads.authenticated;
      let count = 0;

      async function getData() {
        fetch(`/datapulse/api-authentication-updater?service=microsoft_ads`)
          .then((res) => res.json())
          .then((data) => (microsoftAdsStatePlaceholder = data.data))
          .then(function (newData) {
            if (newData == true) {
              setInitialMicrosoftAdsState(false);
            } else {
              setInitialMicrosoftAdsState(true);
            }
          });
      }

      let interval = setInterval(() => {
        if (count > 400) {
          return;
        }
        count = count + 1;
        getData();
        if (initialMicrosoftAdsState !== microsoftAdsStatePlaceholder) {
          setMicrosoftAds((prevState) => ({ ...prevState, authenticated: microsoftAdsStatePlaceholder, title: "Microsoft Ads"  }));
        }
      }, 500);
      return () => {
        clearInterval(interval);
      };
    }
  }, [microsoftAds, authMA]);

  //Facebook Ads
  useEffect(() => {
    if (didMountRefFA.current) {
      let facebookAdsStatePlaceholder = props.api_platforms.facebook_ads.authenticated;
      let count = 0;

      async function getData() {
        fetch(`/datapulse/api-authentication-updater?service=facebook_ads`)
          .then((res) => res.json())
          .then((data) => (facebookAdsStatePlaceholder = data.data))
          .then(function (newData) {
            if (newData == true) {
              setInitialFacebookAdsState(false);
            } else {
              setInitialFacebookAdsState(true);
            }
          });
      }

      let interval = setInterval(() => {
        if (count > 400) {
          return;
        }
        count = count + 1;
        getData();
        if (initialFacebookAdsState !== facebookAdsStatePlaceholder) {
          setFacebookAds((prevState) => ({ ...prevState, authenticated: facebookAdsStatePlaceholder, title: "Facebook Ads" }));
        }
      }, 500);
      return () => {
        clearInterval(interval);
      };
    }
  }, [facebookAds, authFA]);

  //Tiktok Ads
  useEffect(() => {
    if (didMountRefTT.current) {
      let tiktokAdsStatePlaceholder = props.api_platforms.tiktok_ads.authenticated;
      let count = 0;

      async function getData() {
        fetch(`/datapulse/api-authentication-updater?service=tiktok_ads`)
          .then((res) => res.json())
          .then((data) => (tiktokAdsStatePlaceholder = data.data))
          .then(function (newData) {
            if (newData == true) {
              setInitialTiktokAdsState(false);
            } else {
              setInitialTiktokAdsState(true);
            }
          });
      }

      let interval = setInterval(() => {
        if (count > 400) {
          return;
        }
        count = count + 1;
        getData();
        if (initialTiktokAdsState !== tiktokAdsStatePlaceholder) {
          setTiktokAds((prevState) => ({ ...prevState, authenticated: tiktokAdsStatePlaceholder, title: "Tiktok Ads"  }));
        }
      }, 500);
      return () => {
        clearInterval(interval);
      };
    }
  }, [tiktokAds, authTT]);

  return (
    <>
      <Container fluid className="p-0 pt-3 px-4 trim-padding api-access" style={{ backgroundColor: "#E1E1E1" }}>
        <Row className="d-flex mx-0 pt-4 pl-4 pr-4 pb-0 trim-padding">
          <Col className="pl-md-4 pl-0 pr-0 pr-md-3 mb-5 pb-2 basic-info-container" xs={12} style={{ backgroundColor: "#EEEEEE" }}>
            <div className="pt-4 pb-2 mb-3 px-3">
              <h5>Authenticate with the services you need to pull for your clients.</h5>
            </div>
            <Row style={{ maxWidth: "1400px", margin: "0 auto" }} className="mt-5 api-container mb-5">
              {Object.keys(props.api_platforms).map((platformName, index) => {
                let apiTitle =
                  props.api_platforms[platformName].title === "Google Webmasters" ? "Google Search Console" : props.api_platforms[platformName].title;
                let relatedState =
                  platformName === "google_ads"
                    ? googleAds
                    : platformName === "facebook_ads"
                    ? facebookAds
                    : platformName === "google_analytics"
                    ? googleAnalytics
                    : platformName === "microsoft_ads"
                    ? microsoftAds
                    : platformName === "google_webmasters"
                    ? googleWebmasters
                    : tiktokAds;
                return (
                  <Col key={index} xs={12} sm={6} className="text-center api-access-col">
                    <h4>{apiTitle}</h4>
                    {authButton(relatedState)}
                  </Col>
                );
              })}
            </Row>
          </Col>
        </Row>

        <PlatformSpecific showAlert={showAlert} mccId={mccId} setMccId={setMccId} {...props} />
      </Container>
    </>
  );
}
